import { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Box, Flex, Icon, Loading } from 'ui'
import { ArrowDownNarrowWideIcon, ArrowDownWideNarrowIcon } from 'lucide-react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

const StyledInfiniteTable = styled.table<{
  $stickyHeader: boolean
  $borderless: boolean
  $alternateRowColor: boolean
}>`
  ${({ theme, $stickyHeader, $borderless, $alternateRowColor }) => `
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      border: ${$borderless ? '0' : '1px'} solid ${theme.colors.neutrals[100]};
      border-radius: ${theme.radii.standard};
      display: grid;
    
      > thead {
        display: grid;
        ${
          $stickyHeader
            ? `
          position: sticky;
          top: 0;
          z-index: 1;
        `
            : ''
        }
        > tr {
          width: 100%;
          display: flex;
          > th {
            border-bottom: 1px solid ${theme.colors.neutrals[100]};
            font-weight: 500;
            padding: 1rem 1.5rem;
            background-color: ${theme.colors.neutrals[100]};
            color: ${theme.colors.neutrals[400]};
            
            &:first-child {
              border-top-left-radius: ${theme.radii.standard};
            }
            
            &:last-child {
              border-top-right-radius: ${theme.radii.standard};
            }
          }
        }
      }
      
      > tbody {
        display: grid;
        position: relative;

        > tr {  
          > td {
            padding: ${$borderless ? '1rem' : '1.5rem'} 1.5rem;
            vertical-align: middle;
          }
          
          ${
            !$alternateRowColor
              ? `
            &:not(:last-child) {
              position: relative;
              &:after {
                content: '';
                position: absolute;
                left: 1.5rem;
                bottom: 0;
                width: calc(100% - 3rem);
                border-bottom: 1px solid ${theme.colors.neutrals[100]};
              }
            }
          `
              : ''
          }
        }
      }
      
      > tfoot {
        th {
        }
      }
    `}
`

interface InfiniteTableProps<T> {
  data: T[]
  columns: ColumnDef<T, any>[]
  isFetching: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  height: string | number
}

export function InfiniteTable<T>({
  data,
  columns,
  isFetching,
  fetchNextPage,
  hasNextPage,
  height,
}: InfiniteTableProps<T>) {
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        // once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !isFetching &&
          hasNextPage
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, hasNextPage]
  )

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {
      size: Number.MAX_SAFE_INTEGER,
    },
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 50,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element: HTMLElement) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  })

  return (
    <Box
      onScroll={(e) => fetchMoreOnBottomReached(e.currentTarget)}
      ref={tableContainerRef}
      style={{
        overflow: 'auto',
        position: 'relative',
        height,
      }}
    >
      <StyledInfiniteTable
        $stickyHeader
        $borderless={false}
        $alternateRowColor={false}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  {...{
                    colSpan: header.colSpan,
                    style: {
                      width:
                        header.getSize() !== Number.MAX_SAFE_INTEGER
                          ? header.getSize()
                          : undefined,
                      flex:
                        header.getSize() === Number.MAX_SAFE_INTEGER
                          ? 1
                          : undefined,
                    },
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <Flex
                      flexDirection={'row'}
                      alignItems={'center'}
                      style={{
                        cursor: header.column.getCanSort()
                          ? 'pointer'
                          : 'default',
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                      title={
                        header.column.getCanSort()
                          ? header.column.getNextSortingOrder() === 'asc'
                            ? 'Sort ascending'
                            : header.column.getNextSortingOrder() === 'desc'
                            ? 'Sort descending'
                            : 'Clear sort'
                          : undefined
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() ? <>&nbsp;</> : null}
                      {{
                        asc: (
                          <Icon
                            icon={ArrowDownNarrowWideIcon}
                            color={'primary'}
                            size={16}
                          />
                        ),
                        desc: (
                          <Icon
                            icon={ArrowDownWideNarrowIcon}
                            color={'primary'}
                            size={16}
                          />
                        ),
                      }[header.column.getIsSorted() as string] ?? null}
                    </Flex>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index]

            return (
              <tr
                data-index={virtualRow.index}
                key={row.id}
                ref={(node) => rowVirtualizer.measureElement(node)}
                style={{
                  display: 'flex',
                  position: 'absolute',
                  transform: `translateY(${virtualRow.start}px)`,
                  width: '100%',
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    {...{
                      style: {
                        width:
                          cell.column.getSize() !== Number.MAX_SAFE_INTEGER
                            ? cell.column.getSize()
                            : undefined,
                        flex:
                          cell.column.getSize() === Number.MAX_SAFE_INTEGER
                            ? 1
                            : undefined,
                      },
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  {...{
                    colSpan: header.colSpan,
                    style: {
                      width:
                        header.getSize() === Number.MAX_SAFE_INTEGER
                          ? 'auto'
                          : header.getSize(),
                    },
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </StyledInfiniteTable>
      {!!isFetching && (
        <Box p={3}>
          <Loading />
        </Box>
      )}
    </Box>
  )
}
